import type {
  FacilityFloorPlanDto,
  GetFacilityFloorPlanRequestDto,
} from '@/shared/api/modules/floor-plan';
import { FilterSection } from '@/shared/components';
import { useTableSortingState } from '@/shared/lib/hooks';
import { Button, Table, Tag, type SelectOption } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import capitalize from 'lodash.capitalize';
import { useEffect, type FC } from 'react';
import { getFacilityFloorPlanQueryOptions } from '../api/get-facility-floor-plan.query';
import { FloorPlanStatus } from '../model/contracts';
import { useFacilityFloorPlanFiltersStore } from '../model/store';
import { FacilityFloorPlanFiltersForm } from './facility-floor-plan-filters-form';
import { FloorPlanHistorySubTable } from './facility-floor-plan-history-subtable';

const columnHelper = createColumnHelper<FacilityFloorPlanDto>();

type FacilityFloorPlanTableProps = {
  /** Omit for the portfolio view — beds across every accessible facility. */
  facilityId?: number;
  /**
   * Portfolio-mode Facility filter options. Passing them turns the filter on
   * inside the table's own filter panel, alongside Status.
   */
  facilitiesOptions?: SelectOption[];
};

const columnIdToSortField: Record<
  string,
  NonNullable<GetFacilityFloorPlanRequestDto['sortBy']>
> = {
  bedNumber: 'bedNumber',
  roomNumber: 'roomNumber',
  floorName: 'floor',
  isOccupied: 'status',
  hall: 'hall',
  facilityName: 'facility',
};

const facilityColumn = columnHelper.accessor('facilityName', {
  enableSorting: true,
  header: 'Facility',
  maxSize: 220,
  cell: ({ getValue }) => getValue() ?? 'N/A',
  footer: (info) => info.column.id,
});

const baseColumns = [
  columnHelper.accessor('roomNumber', {
    enableSorting: true,
    header: 'Room #',
    cell: ({ getValue }) => getValue() ?? 'N/A',
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('bedNumber', {
    enableSorting: true,
    header: 'Bed',
    maxSize: 200,
    cell: ({ getValue }) => getValue() ?? 'N/A',
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('isOccupied', {
    enableSorting: true,
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue()
        ? FloorPlanStatus.OCCUPIED
        : FloorPlanStatus.VACANT;
      return (
        <Tag
          text={capitalize(status.toLowerCase())}
          variant={status === FloorPlanStatus.VACANT ? 'green' : 'red'}
        />
      );
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('primaryOccupantFullName', {
    enableSorting: false,
    header: 'Resident Name',
    maxSize: 200,
    cell: ({ getValue, row }) => {
      const primary = getValue();
      const secondary = row.original.secondOccupantFullName;
      if (primary && secondary) {
        return `${primary}, ${secondary}`;
      }
      return primary || secondary || '-';
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('floorName', {
    enableSorting: true,
    header: 'Floor',
    maxSize: 200,
    cell: ({ getValue }) => getValue() ?? 'N/A',
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('hall', {
    enableSorting: true,
    header: 'Hall',
    maxSize: 200,
    cell: ({ getValue }) => {
      const value = getValue();
      if (value == null) return '-';
      if (typeof value === 'string' && value.trim() === '') return '-';
      return value;
    },
    footer: (info) => info.column.id,
  }),
  columnHelper.accessor('id', {
    enableSorting: false,
    header: '',
    size: 40,
    cell: (args) => (
      <div className="flex flex-1 items-center justify-end">
        <Button
          iconed
          size="small"
          variant="text"
          leftIconName="chevron-down"
          className={
            args.row.getIsExpanded() ? 'rotate-180 transition-transform' : ''
          }
          onClick={args.row.getToggleExpandedHandler()}
        />
      </div>
    ),
  }),
] as ColumnDef<FacilityFloorPlanDto>[];

/**
 * Single Mode hides the Facility column — every row is the same facility, so it
 * is dead weight. Portfolio Mode leads with it.
 */
const getColumns = (showFacilityColumn: boolean) =>
  (showFacilityColumn
    ? [facilityColumn as ColumnDef<FacilityFloorPlanDto>, ...baseColumns]
    : baseColumns) as ColumnDef<FacilityFloorPlanDto>[];

export const FacilityFloorPlanTable: FC<FacilityFloorPlanTableProps> = ({
  facilityId,
  facilitiesOptions,
}) => {
  const isPortfolioMode = facilityId === undefined;

  const { sortingState, setSortingState, sortBy, sortDirection } =
    useTableSortingState<
      | 'bedNumber'
      | 'roomNumber'
      | 'floorName'
      | 'isOccupied'
      | 'hall'
      | 'facilityName'
    >({
      sortBy: 'isOccupied',
      sortDirection: 'desc',
    });

  const columns = getColumns(isPortfolioMode);

  const { search, setFilters, currentPage, statuses, facilities } =
    useFacilityFloorPlanFiltersStore();

  useEffect(() => {
    useFacilityFloorPlanFiltersStore.setState(
      useFacilityFloorPlanFiltersStore.getInitialState()
    );
  }, [facilityId]);

  const mappedSortBy = sortBy ? columnIdToSortField[sortBy] : undefined;

  const statusFilter =
    statuses && statuses.length > 0
      ? statuses.length === 1 && statuses[0].value === 'VACANT'
        ? false
        : statuses.length === 1 && statuses[0].value === 'OCCUPIED'
          ? true
          : undefined
      : undefined;

  const facilitiesFilter = facilities?.length
    ? facilities.map((facility) => +facility.value)
    : undefined;

  const {
    data: floorPlanData,
    isPending,
    isError,
  } = useQuery(
    getFacilityFloorPlanQueryOptions({
      facilityId,
      facilitiesIds: isPortfolioMode ? facilitiesFilter : undefined,
      sortBy: mappedSortBy,
      sortDirection,
      page: currentPage,
      perPage: 10,
      status: statusFilter,
      search,
    })
  );

  /**
   * The "of N" denominator: beds in the current facility scope, ignoring the
   * user's own filters.
   *
   * /floor-plan only ever returns the count matching the query it was given —
   * status, search and facilities all narrow `meta.total` — so the
   * denominator needs its own request carrying the scope and nothing else.
   * Passing the filtered count as both halves rendered "11 beds of 11".
   */
  const { data: scopeTotal } = useQuery(
    getFacilityFloorPlanQueryOptions({
      facilityId,
      perPage: 1,
      page: 1,
    })
  );
  const totalInScope = scopeTotal?.meta.total;

  const getFiltersLabel = () => {
    const labels: string[] = [];

    if (facilities?.length)
      labels.push(facilities.map((f) => f.label).join(', '));
    if (statuses?.length) labels.push(statuses.map((s) => s.label).join(', '));

    return labels.length > 0 ? labels.join('; ') : '';
  };

  return (
    <div>
      <FilterSection
        title="Floor Plan"
        getFiltersLabel={getFiltersLabel}
        counterLabel={{ singular: 'bed', plural: 'beds' }}
        itemsCount={{
          total: totalInScope ?? floorPlanData?.meta.total ?? 0,
          filtered: floorPlanData?.meta.total ?? 0,
        }}
        search={{
          value: search,
          placeholder: 'Search room',
          onChange: (search) => {
            setFilters({ search, currentPage: 1 });
          },
        }}
        form={
          <FacilityFloorPlanFiltersForm
            isLoading={isPending}
            facilitiesOptions={isPortfolioMode ? facilitiesOptions : undefined}
          />
        }
      />
      <Table
        striped
        headerSeparated
        columns={columns}
        errored={isError}
        sortingState={sortingState}
        getRowCanExpand={() => true}
        data={floorPlanData?.data ?? []}
        emptyState={!floorPlanData?.data?.length && !isPending}
        renderSubComponent={({ row }) => (
          <div className="w-[770px] p-3">
            <FloorPlanHistorySubTable floorPlanId={row.original.id} />
          </div>
        )}
        pagination={{
          currentPage: floorPlanData?.meta.page ?? 1,
          totalPages: floorPlanData?.meta.totalPages ?? 1,
          onPageChange: (page) => setFilters({ currentPage: page }),
        }}
        loading={isPending}
        onSortingChange={setSortingState}
      />
    </div>
  );
};
